import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { Element, scroller } from 'react-scroll';
import { Alert, Form } from 'react-bootstrap';
import useNavProject from '../../../hooks/useNavProject';
import useAPI from '../../../hooks/useAPI';
import useUserContext from '../../../hooks/useUserContext';
import useToast from '../../../hooks/useToast';
import { status as PS } from '../../../helpers/passport';
import { stepScroll } from '../../../helpers';
import BackBtn from '../../../components/UI/BackBtn';
import ProjectScrollBox from '../../../components/project/Partials/ProjectScrollBox';
import StatusBadge from '../../../components/project/PassportRead/StatusBadge';
import CommonInfo from '../../../components/project/PassportRead/CommonInfo';
import Contents from '../../../components/project/PassportRead/Contents';
import Results from '../../../components/project/PassportRead/Results';
import Indicators from '../../../components/project/PassportRead/Indicators';
import Budget from '../../../components/project/PassportRead/Budget';
import Participants from '../../../components/project/PassportRead/Participants';
import ExpertDecisionCard from '../../../components/project/PassportRead/ExpertDecisionCard';
import ButtonsCard from './partials/ButtonsCard';
import ModalRevisionExpert from '../../../components/project/PassportRead/ModalRevisionExpert';
import ModalUploadScan from '../../../components/project/PassportRead/ModalUploadScan';
import ModalCreateNewVersion from '../../../components/project/PassportRead/ModalCreateNewVersion';
import ConfirmModal from '../../../components/common/ConfirmModal';
import { getActionsModel } from '../../../services/roles/policyProjRoleStatus';
import { isAdmin } from '../../../services/rolesSvc';
import Attachments from '../../../components/project/PassportRead/Attachments';

export default function PolicyProjectPassportRead({
  mngData, policyId, projId, enableEditMode, isLatestVersion,
}) {
  const navProject = useNavProject();
  const api = useAPI();
  const userCtx = useUserContext();
  const addToast = useToast();
  const formEl = useRef(null);
  const history = useHistory();

  const [proj, setProj] = useState(null);
  const [acceptedProj, setAcceptedProj] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const can = useMemo(() => (proj ? getActionsModel(proj.status, userCtx, policyId, proj.origins.originProjId, isLatestVersion) : {}), [proj]);

  useEffect(() => {
    getProj(projId);
  }, []);
  const getProj = async (id) => {
    await setLoading(true);
    if (id) {
      const resProj = await api.get(`/projs/${id}?readonly=true`);
      const respProjectStructure = await api.get(`/structure/projs/${id}`);
      if (resProj.error) {
        setError(resProj.error);
        setLoading(false);
        return;
      }
      if (respProjectStructure.error) {
        setError(respProjectStructure.error);
        setLoading(false);
        return;
      }
      const policyName = respProjectStructure.policy.name;

      navProject.setProjTitle(respProjectStructure.name);
      navProject.setPolicyTitle(policyName);

      setProj({
        ...resProj,
        policyName,
        startDate: resProj.startDate ? resProj.startDate.substring(0, 10) : undefined,
        endDate: resProj.endDate ? resProj.endDate.substring(0, 10) : undefined,
        approvedOn: resProj.approvedOn ? resProj.approvedOn.substring(0, 10) : undefined,
        attachments: resProj.attachments.sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn)) || [],
      });
    } else {
      const resPolicies = await api.get('/mng/strategies?isPortfolio=true');
      const policyName = proj.policyId ? resPolicies.find((s) => s.id === parseInt(proj.policyId)).name : '';

      navProject.setProjTitle('Новый проект');
      navProject.setPolicyTitle(policyName);

      setProj({
        ...proj,
        strategyName: 'Отсутствует',
        policyName,
      });
    }
    await setLoading(false);
  };

  const updateProjStatus = (status) => setProj((prev) => ({ ...prev, status }));

  const [isModalSendToCommittee, setIsModalSendToCommittee] = useState(false);
  const [isModalRevisionCommittee, setIsModalRevisionCommittee] = useState(false);
  const [isModalUpload, setIsModalUpload] = useState(false);
  const [isModalCreateNewVersion, setIsModalCreateNewVersion] = useState(false);
  const [isModalDeleteProj, setIsModalDeleteProj] = useState(false);

  const handleSendToCommittee = () => setIsModalSendToCommittee(true);
  const handleReturnForRevisionCommittee = () => setIsModalRevisionCommittee(true);
  const handleApproveFinal = () => setIsModalUpload(true);

  const handleDelete = () => setIsModalDeleteProj(true);

  const apiDelete = async () => {
    const result = await api.del(`/projs/${projId}`, { id: projId });
    setIsModalDeleteProj(false);
    if (result && !result.error) {
      addToast.success('Проект успешно удален');
      history.push(`/policies/${policyId}?t=subprojectconnection`);
    }
  };

  const apiSendToCommittee = async () => {
    const response = await api.post(`/projs/${projId}/sendToCommittee`);
    if (!response.error && response.status) {
      setProj((prev) => ({ ...prev, ...response }));
      setIsModalSendToCommittee(false);
      addToast.success('Успешно отправлен в Проектный офис');
      window.scroll({ top: 0, left: 0, behavior: 'smooth' });
    }
  };

  const handleExpertsDecision = async ({ isApproved, recommendations, protocol, mark }) => {
    const response = await api.post(`/projs/${projId}/expertDecision`, { isApproved, recommendations, protocol, mark });
    if (!response.error) {
      setProj((prev) => ({
        ...prev,
        status: response.status,
        expertGroupDecision: response.expertGroupDecision,
        expertGroupProtocol: response.expertGroupProtocol,
        expertGroupMark: response.expertGroupMark,
        commentHead: null,
        commentExpert: null,
        commentCommittee: null,
      }));
      addToast.success('Решение сохранено');
    }
  };

  const apiReturnForRevision = async (comment) => {
    const response = await api.post(`/projs/${projId}/return`, { comment });
    if (!response.error) {
      setProj((prev) => ({ ...prev, ...response }));
      setIsModalRevisionCommittee(false);
      window.scroll({ top: 0, left: 0, behavior: 'smooth' });
    }
  };

  const handleEdit = (fromCreateNewVersionModal) => {
    if ([PS.approved, PS.committeeDeclined].includes(proj.status)) {
      if (fromCreateNewVersionModal) {
        enableEditMode(true);
      } else {
        setIsModalCreateNewVersion(true);
      }
    } else {
      enableEditMode();
    }
  };

  const uploadScan = async (file) => {
    const response = await api.postFormData(
      `/projs/${projId}/uploadScan`,
      {},
      file,
        {isJsonValue:false}
    );

    if (response && !response.error) {
      setProj((prev) => ({ ...prev, ...response }));
      addToast.success('Скан успешно загружен');
      return true;
    }
    addToast.error('При загрузке скана произошла ошибка');
    return false;
  };

  if (error) {
    return (
      <div style={{ color: 'red', background: 'white' }}>
        Ошибка:
        {error.message}
      </div>
    );
  }
  if (loading) return <section className="page form-page project-card-page"><div className="container">Загрузка...</div></section>;

  return (
    <>
      <section className="page form-page project-card-page">
        <div className="container">
          <div style={{ marginBottom: 16 }}>
            <BackBtn />
          </div>

          <ProjectScrollBox />

          <div style={styledContainer}>
            <h3 style={styledHeading}>Цифровой паспорт</h3>
            <div><StatusBadge status={proj.status} /></div>
          </div>

          {proj.status === PS.committeeApproved && <Alert variant="warning" style={{ whiteSpace: 'pre-wrap' }}>Необходимо разместить подписанный скан Цифрового паспорта. Для размещения используйте кнопку &quot;Действия&quot;</Alert> }
          { proj.commentCommittee && (
          <Alert variant="danger" style={{ whiteSpace: 'pre-wrap' }}>
            <strong>Комментарий Цифрового комитета</strong>
            <br />
            <br />
            {proj.commentCommittee}
          </Alert>
          ) }

          <Form noValidate ref={formEl}>
            <Element id="CommonInfo" name="CommonInfo">
              <CommonInfo data={proj} types={mngData.projTypes} />
            </Element>
            <Element id="Contents" name="Contents">
              <Contents data={proj} />
            </Element>
            <Element id="Results" name="Results">
              <Results data={proj} letters={mngData.letters} resultTypes={mngData.resultTypes}/>
            </Element>
            <Element id="Indicators" name="Indicators">
              <Indicators data={proj} />
            </Element>
            <Element id="Budget" name="Budget">
              <Budget data={proj.plans} finSources={mngData.finSources} />
            </Element>
            <Element id="Participants" name="Participants">
              <Participants title="Команда управления" data={proj.users} userRoles={mngData.userRoles} />
            </Element>
            <Element id="Participants2" name="Participants2">
              <Participants title="Согласующие" data={proj.approvers} userRoles={mngData.userRoles} />
            </Element>
            {proj.attachments.length !== 0 && <Element id="Attachments" name="Attachments">
              <Attachments data={proj}/>
            </Element>}
            {[PS.onApproveCommittee, PS.committeeApproved, PS.committeeDeclined, PS.approved].includes(proj.status)
              && (
                <ExpertDecisionCard
                  cardTitle="Решение Цифрового комитета"
                  className="mb-4"
                  readOnly={[PS.committeeApproved, PS.committeeDeclined, PS.approved].includes(proj.status)}
                  canEdit={isAdmin(userCtx)}
                  onExpertsDecision={handleExpertsDecision}
                  proj={proj}
                />
              )}
            <ButtonsCard
              can={can}
              projId={projId}
              originId={proj.origins?.originProjId}
              status={proj.status}
              onEdit={handleEdit}
              onEditCurrent={() => enableEditMode()}
              onEditMilestones={() => enableEditMode(false, true)}
              onSendCommittee={handleSendToCommittee}
              onReturnForRevisionCommittee={handleReturnForRevisionCommittee}
              onApproveFinal={handleApproveFinal}
              onDelete={handleDelete}
            />
          </Form>
        </div>
      </section>
      <ConfirmModal
        show={isModalSendToCommittee}
        onClose={() => setIsModalSendToCommittee(false)}
        onConfirm={apiSendToCommittee}
        headerText="Отправка проекта"
        message="Проект будет направлен в Проектный офис, убедитесь, что поля заполнены верно. Отменить действие будет нельзя"
      />
      <ModalRevisionExpert
        show={isModalRevisionCommittee}
        onSubmit={apiReturnForRevision}
        onClose={() => setIsModalRevisionCommittee(false)}
      />
      <ModalUploadScan
        show={isModalUpload}
        onShow={() => setIsModalUpload(true)}
        onClose={() => setIsModalUpload(false)}
        onSave={uploadScan}
        scanFile={proj.minioScanFile}
        userCtx={userCtx}
      />
      <ModalCreateNewVersion
        show={isModalCreateNewVersion}
        onConfirm={() => { handleEdit(true); setIsModalCreateNewVersion(false); }}
        onClose={() => setIsModalCreateNewVersion(false)}
      />
      <ConfirmModal
        show={isModalDeleteProj}
        onClose={() => setIsModalDeleteProj(false)}
        onConfirm={apiDelete}
        headerText="Удаление проекта"
        message="Проект будет удален, данное действие нельзя отменить. Удалить проект?"
      />
    </>
  );
}

const styledHeading = { marginBottom: 0, textAlign: 'left' };
const styledContainer = { display: 'flex', marginTop: 36, marginBottom: 24, justifyContent: 'space-between' };
